import { useState } from "react";
import { postSubscriber } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal, ArchReveal } from "@/components/motion";
import { Slider } from "@/components/ui/slider";
import { formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Gem, Sparkles } from "lucide-react";

const TIERS = [
  { icon: Sparkles, name: "Silver", range: "0 – 4.999 puan", perks: ["Doğum günü sürprizi", "Yeni koleksiyonlara erken erişim", "Üyelere özel seçkiler"] },
  { icon: Gem, name: "Gold", range: "5.000 – 14.999 puan", perks: ["Her siparişte ücretsiz kargo", "Özel davet ve lansmanlar", "Öncelikli müşteri desteği"] },
  { icon: Crown, name: "Velvet", range: "15.000+ puan", perks: ["Kişisel stil danışmanı", "Atölye buluşmalarına davet", "Yıllık özel hediye"] },
];

const MULTIPLIER = { Silver: 1, Gold: 1.5, Velvet: 2 };

export default function Club() {
  const [email, setEmail] = useState("");
  const [spend, setSpend] = useState([3000]);
  const [loading, setLoading] = useState(false);

  const tier = spend[0] >= 10000 ? "Velvet" : spend[0] >= 4000 ? "Gold" : "Silver";
  const points = Math.round((spend[0] / 10) * MULTIPLIER[tier]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await postSubscriber({ email, source: "club" });
      toast.success(r.message);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bir sorun oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Shule Club" description="Anların biriktikçe ayrıcalıkların artsın. Shule Club'a katıl." path="/shule-club" />
      <section className="bg-shule-pudra/40 border-b border-shule-line">
        <div className="container-shule py-16 lg:py-24 text-center max-w-2xl mx-auto">
          <Reveal>
            <p className="eyebrow">Shule Club</p>
            <h1 className="mt-3 font-serif text-5xl lg:text-6xl font-light text-shule-coffee text-balance">
              Anların biriktikçe, <span className="italic text-shule-goldhover">ayrıcalıkların</span> artsın
            </h1>
            <p className="mt-5 text-shule-mocha leading-relaxed">
              Üyelik ücretsiz. Her alışveriş puan, her puan yeni bir ayrıcalık demek.
            </p>
            <form data-testid="club-page-form" onSubmit={submit} className="mt-8 flex max-w-md mx-auto rounded-full border border-shule-lineaccent bg-shule-paper overflow-hidden focus-within:border-shule-gold transition-colors">
              <input data-testid="club-page-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="E-posta adresin" className="flex-1 min-w-0 bg-transparent px-5 text-sm outline-none min-h-[52px]" />
              <button data-testid="club-page-join-button" disabled={loading} className="px-6 bg-shule-coffee text-shule-paper text-sm font-semibold hover:bg-shule-goldhover transition-colors">
                Hemen Katıl
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <section className="container-shule py-16 lg:py-24">
        <Reveal className="mb-10 text-center">
          <p className="eyebrow">Üyelik Seviyeleri</p>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl">Üç seviye, üç ayrı dünya</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {TIERS.map((t, i) => (
            <ArchReveal key={t.name} delay={i * 0.1}>
              <div data-testid={`club-tier-card-${t.name.toLowerCase()}`}
                className={`arch-card p-8 pt-12 text-center h-full ${i === 2 ? "bg-shule-goldlight/40 border-shule-gold/40" : "bg-shule-paper"}`}>
                <t.icon className="w-6 h-6 mx-auto text-shule-gold" />
                <p className="mt-4 font-serif text-3xl">{t.name}</p>
                <p className="text-[11px] uppercase tracking-wider text-shule-soft mt-1">{t.range}</p>
                <ul className="mt-5 space-y-2 text-sm text-shule-mocha text-left">
                  {t.perks.map((p) => (
                    <li key={p} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full bg-shule-gold mt-1.5 shrink-0" />{p}</li>
                  ))}
                </ul>
              </div>
            </ArchReveal>
          ))}
        </div>

        <Reveal className="mt-16 max-w-2xl mx-auto rounded-[2rem] border border-shule-line bg-shule-paper p-8 sm:p-10 shadow-[0_10px_30px_-10px_rgba(61,46,40,0.05)]">
          <p className="eyebrow">Puan Hesaplayıcı</p>
          <h3 className="mt-2 font-serif text-2xl">Yıllık alışverişine göre tahmini puanın</h3>
          <p data-testid="points-spend-value" className="mt-6 text-sm text-shule-mocha">Yıllık alışveriş: <span className="font-bold text-shule-coffee">{formatPrice(spend[0])}</span></p>
          <Slider data-testid="points-slider" value={spend} onValueChange={setSpend} min={500} max={20000} step={500} className="mt-4" />
          <div className="mt-6 flex items-center justify-between rounded-2xl bg-shule-cream px-5 py-4">
            <span className="text-sm text-shule-mocha">Tahmini seviye: <strong>{tier}</strong></span>
            <span data-testid="points-result" className="font-serif text-3xl text-shule-goldhover">{points.toLocaleString("tr-TR")} puan</span>
          </div>
          <p className="mt-3 text-[11px] text-shule-soft">Demo hesaplayıcıdır; gerçek puan katsayıları üyelik sözleşmesinde belirtilir.</p>
        </Reveal>
      </section>
    </>
  );
}
